import { useCallback, useState } from 'react';

import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

export const useAVNameEdit = (
  analyticalVariable: AnalyticalVariable | undefined,
  onNameChange: (updates: Partial<AnalyticalVariable>) => void
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [validationError, setValidationError] = useState<string>();

  const startEdit = useCallback(() => {
    setEditedName(analyticalVariable?.name ?? '');
    setValidationError(undefined);
    setIsEditing(true);
  }, [analyticalVariable?.name]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setValidationError(undefined);
  }, []);

  const validate = useCallback(
    (name: string): string | undefined => {
      const trimmed = name.trim();
      if (!trimmed) return 'Name cannot be empty';
      const allAVs = useCohortDefinitionStore.getState().analyticalVariables;
      const duplicate = allAVs.some(
        (av) => av.id !== analyticalVariable?.id && av.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (duplicate) return 'Name must be unique in this cohort';
      return undefined;
    },
    [analyticalVariable?.id]
  );

  const saveEdit = useCallback(() => {
    const error = validate(editedName);
    if (error) {
      setValidationError(error);
      return;
    }
    onNameChange({ name: editedName.trim() });
    setIsEditing(false);
    setValidationError(undefined);
  }, [editedName, validate, onNameChange]);

  const handleNameChange = useCallback(
    (value: string) => {
      setEditedName(value);
      if (validationError) setValidationError(validate(value));
    },
    [validationError, validate]
  );

  return {
    isEditing,
    editedName,
    validationError,
    startEdit,
    cancelEdit,
    saveEdit,
    handleNameChange,
  };
};
